import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Truck, Plus, Factory, Camera, X, Upload, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const EquipmentEditDrawer = ({
  open,
  onOpenChange,
  equipment,
  suppliers,
  equipmentTypes,
  equipmentMakes = [],
  equipmentModels = [],
  onSubmit,
  onSupplierCreate,
  onMakeCreate,
  onModelCreate,
  onPhotoUpload,
  onPhotoDelete,
  onFetchModels,
  defaultSupplierId = null,
  api
}) => {
  const [form, setForm] = useState({
    asset_id: '',
    supplier_id: '',
    equipment_type: '',
    capacity: '',
    status: 'available',
    notes: '',
    // New fields
    make_id: '',
    model_id: '',
    year: '',
    gvm: '',
    serial_number: '',
    rego_number: '',
    hours_kms: '',
    hours_kms_2: ''
  });

  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Quick Add Dialogs
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  
  const [supplierForm, setSupplierForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: ''
  });

  const [makeForm, setMakeForm] = useState({ name: '' });
  const [modelForm, setModelForm] = useState({ name: '' });

  // Get selected model name for display
  const getSelectedModelName = () => {
    if (!form.model_id) return null;
    const model = equipmentModels.find(m => m.id === form.model_id);
    return model?.name || null;
  };

  // Filter models by selected make
  const filteredModels = form.make_id 
    ? equipmentModels.filter(m => m.make_id === form.make_id)
    : [];

  useEffect(() => {
    if (equipment) {
      setForm({
        asset_id: equipment.asset_id || '',
        supplier_id: equipment.supplier_id || '',
        equipment_type: equipment.equipment_type || '',
        capacity: equipment.capacity || '',
        status: equipment.status || 'available',
        notes: equipment.notes || '',
        make_id: equipment.make_id || '',
        model_id: equipment.model_id || '',
        year: equipment.year || '',
        gvm: equipment.gvm || '',
        serial_number: equipment.serial_number || '',
        rego_number: equipment.rego_number || '',
        hours_kms: equipment.hours_kms || '',
        hours_kms_2: equipment.hours_kms_2 || ''
      });
      setPhotos(equipment.photos || []);
      
      // Fetch models for existing make
      if (equipment.make_id && onFetchModels) {
        onFetchModels(equipment.make_id);
      }
    } else {
      setForm({
        asset_id: '',
        supplier_id: defaultSupplierId || '',
        equipment_type: '',
        capacity: '',
        status: 'available',
        notes: '',
        make_id: '',
        model_id: '',
        year: '',
        gvm: '',
        serial_number: '',
        rego_number: '',
        hours_kms: '',
        hours_kms_2: ''
      });
      setPhotos([]);
    }
  }, [equipment, defaultSupplierId, open]);

  const handleSupplierSelect = (value) => {
    if (value === '__add_new__') {
      setSupplierForm({ company_name: '', contact_name: '', phone: '', email: '' });
      setSupplierDialogOpen(true);
    } else {
      setForm({ ...form, supplier_id: value });
    }
  };

  const handleMakeSelect = (value) => {
    if (value === '__add_new__') {
      setMakeForm({ name: '' });
      setMakeDialogOpen(true);
    } else {
      setForm({ ...form, make_id: value, model_id: '' });
      // Fetch models for this make
      if (onFetchModels) {
        onFetchModels(value);
      }
    }
  };

  const handleModelSelect = (value) => {
    if (value === '__add_new__') {
      setModelForm({ name: '' });
      setModelDialogOpen(true);
    } else {
      setForm({ ...form, model_id: value });
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (onSupplierCreate) {
      const newSupplier = await onSupplierCreate(supplierForm);
      if (newSupplier) {
        setForm({ ...form, supplier_id: newSupplier.id });
        setSupplierDialogOpen(false);
      }
    }
  };

  const handleCreateMake = async (e) => {
    e.preventDefault();
    if (onMakeCreate) {
      const newMake = await onMakeCreate(makeForm);
      if (newMake) {
        setForm({ ...form, make_id: newMake.id, model_id: '' });
        setMakeDialogOpen(false);
      }
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    if (onModelCreate && form.make_id) {
      const newModel = await onModelCreate({ ...modelForm, make_id: form.make_id });
      if (newModel) {
        setForm({ ...form, model_id: newModel.id });
        setModelDialogOpen(false);
      }
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (photos.length >= 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      if (equipment?.id && onPhotoUpload) {
        const newPhoto = await onPhotoUpload(equipment.id, file);
        if (newPhoto) {
          setPhotos([...photos, newPhoto]);
        }
      } else {
        // For new equipment, just show preview (upload after create)
        const reader = new FileReader();
        reader.onload = (event) => {
          setPhotos([...photos, {
            url: event.target.result,
            filename: file.name,
            isLocal: true,
            file: file
          }]);
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoDelete = async (index) => {
    const photo = photos[index];
    
    if (photo.isLocal) {
      // Remove local preview
      setPhotos(photos.filter((_, i) => i !== index));
    } else if (equipment?.id && onPhotoDelete) {
      try {
        await onPhotoDelete(equipment.id, index);
        setPhotos(photos.filter((_, i) => i !== index));
      } catch (error) {
        toast.error('Failed to delete photo');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.supplier_id) {
      return;
    }
    
    // Convert numeric fields
    const submitData = {
      ...form,
      year: form.year ? parseInt(form.year) : null,
      hours_kms: form.hours_kms ? parseFloat(form.hours_kms) : null,
      hours_kms_2: form.hours_kms_2 ? parseFloat(form.hours_kms_2) : null,
      // Include local photos for upload after create
      _localPhotos: photos.filter(p => p.isLocal).map(p => p.file)
    };
    
    onSubmit(submitData, equipment);
  };

  const isFormValid = form.asset_id && form.equipment_type && form.supplier_id;
  const isEditing = !!equipment;

  // Get make name for display
  const getMakeName = (makeId) => {
    const make = equipmentMakes.find(m => m.id === makeId);
    return make?.name || '';
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[900px] sm:max-w-[900px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              {isEditing ? 'Edit Asset' : 'Add New Asset'}
            </SheetTitle>
          </SheetHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 py-4" data-testid="equipment-form">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Basic Information</h3>
              
              <div className="space-y-2">
                <Label className="label-overline">Supplier (Owner) *</Label>
                <Select value={form.supplier_id || ''} onValueChange={handleSupplierSelect}>
                  <SelectTrigger data-testid="equipment-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {onSupplierCreate && (
                      <>
                        <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                          <span className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            ADD NEW SUPPLIER
                          </span>
                        </SelectItem>
                        {suppliers.length > 0 && <div className="border-t border-border my-1" />}
                      </>
                    )}
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Asset ID *</Label>
                  <Input
                    data-testid="equipment-asset-input"
                    value={form.asset_id}
                    onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
                    placeholder="EQ-001"
                    className="font-mono"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Type *</Label>
                  <Select
                    value={form.equipment_type}
                    onValueChange={(value) => setForm({ ...form, equipment_type: value })}
                  >
                    <SelectTrigger data-testid="equipment-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Capacity</Label>
                  <Input
                    data-testid="equipment-capacity-input"
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    placeholder="50 tons"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Details Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Vehicle Details</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Make</Label>
                  <Select value={form.make_id || ''} onValueChange={handleMakeSelect}>
                    <SelectTrigger data-testid="equipment-make-select">
                      {form.make_id && getMakeName(form.make_id) ? (
                        <span>{getMakeName(form.make_id)}</span>
                      ) : (
                        <SelectValue placeholder="Select make" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {onMakeCreate && (
                        <>
                          <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                            <span className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              ADD NEW MAKE
                            </span>
                          </SelectItem>
                          {equipmentMakes.length > 0 && <div className="border-t border-border my-1" />}
                        </>
                      )}
                      {equipmentMakes.map((make) => (
                        <SelectItem key={make.id} value={make.id}>{make.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Model</Label>
                  <Select 
                    value={form.model_id || ''} 
                    onValueChange={handleModelSelect}
                    disabled={!form.make_id}
                  >
                    <SelectTrigger data-testid="equipment-model-select">
                      {form.model_id && getSelectedModelName() ? (
                        <span>{getSelectedModelName()}</span>
                      ) : (
                        <SelectValue placeholder={form.make_id ? "Select model" : "Select make first"} />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {onModelCreate && form.make_id && (
                        <>
                          <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                            <span className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              ADD NEW MODEL
                            </span>
                          </SelectItem>
                          {filteredModels.length > 0 && <div className="border-t border-border my-1" />}
                        </>
                      )}
                      {filteredModels.map((model) => (
                        <SelectItem key={model.id} value={model.id}>{model.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Year</Label>
                  <Input
                    data-testid="equipment-year-input"
                    type="number"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    placeholder="2024"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">GVM</Label>
                  <Input
                    data-testid="equipment-gvm-input"
                    value={form.gvm}
                    onChange={(e) => setForm({ ...form, gvm: e.target.value })}
                    placeholder="12,000 kg"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Serial #</Label>
                  <Input
                    data-testid="equipment-serial-input"
                    value={form.serial_number}
                    onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                    placeholder="VIN/Serial number"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Rego #</Label>
                  <Input
                    data-testid="equipment-rego-input"
                    value={form.rego_number}
                    onChange={(e) => setForm({ ...form, rego_number: e.target.value })}
                    placeholder="ABC-123"
                    className="font-mono uppercase"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Hours/Kms</Label>
                  <Input
                    data-testid="equipment-hours-input"
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.hours_kms}
                    onChange={(e) => setForm({ ...form, hours_kms: e.target.value })}
                    placeholder="Primary meter reading"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">2nd Hours/Kms</Label>
                  <Input
                    data-testid="equipment-hours2-input"
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.hours_kms_2}
                    onChange={(e) => setForm({ ...form, hours_kms_2: e.target.value })}
                    placeholder="Secondary meter reading"
                  />
                </div>
              </div>
            </div>

            {/* Status & Notes Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Status & Notes</h3>
              
              <div className="space-y-2">
                <Label className="label-overline">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger data-testid="equipment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="booked">Booked</SelectItem>
                    <SelectItem value="out_of_service">Out of Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  data-testid="equipment-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </div>

            {/* Photos Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Asset Photos ({photos.length}/5)
                </h3>
                {photos.length < 5 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    data-testid="upload-photo-btn"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4 mr-2" />
                    )}
                    Add Photo
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>

              {photos.length > 0 ? (
                <div className="grid grid-cols-5 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-muted">
                      <img
                        src={photo.url}
                        alt={photo.filename || `Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handlePhotoDelete(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-photo-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {photo.isLocal && (
                        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded">
                          Pending
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No photos yet. Click "Add Photo" to upload.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPEG, PNG, or WebP. Max 10MB each.
                  </p>
                </div>
              )}
            </div>

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="equipment-submit-btn" disabled={!isFormValid}>
                {isEditing ? 'Update' : 'Add'} Asset
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Quick Add Supplier Dialog */}
      {onSupplierCreate && (
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Factory className="h-5 w-5 text-[#ffd108]" />
                Quick Add Supplier
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSupplier} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Company Name *</Label>
                <Input
                  value={supplierForm.company_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, company_name: e.target.value })}
                  placeholder="ABC Equipment Hire"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Contact Name *</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Phone</Label>
                  <Input
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    type="email"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                    placeholder="john@supplier.com"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt">
                  Create Supplier
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick Add Make Dialog */}
      {onMakeCreate && (
        <Dialog open={makeDialogOpen} onOpenChange={setMakeDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Truck className="h-5 w-5 text-[#ffd108]" />
                Add Equipment Make
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMake} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Make Name *</Label>
                <Input
                  value={makeForm.name}
                  onChange={(e) => setMakeForm({ ...makeForm, name: e.target.value })}
                  placeholder="e.g., Caterpillar, Liebherr, Kenworth"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setMakeDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt">
                  Create Make
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Quick Add Model Dialog */}
      {onModelCreate && (
        <Dialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Truck className="h-5 w-5 text-[#ffd108]" />
                Add Equipment Model
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateModel} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Make</Label>
                <Input
                  value={getMakeName(form.make_id)}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Model Name *</Label>
                <Input
                  value={modelForm.name}
                  onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                  placeholder="e.g., 320D, LTM 1060, T909"
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setModelDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt">
                  Create Model
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default EquipmentEditDrawer;
